import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type React from "react";
import { type CSSProperties, type Dispatch, type FC, Fragment, type SetStateAction, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthProvider";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useJobs } from "@/hooks/useJobs";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { localStorageService } from "@/services/LocalStorageService";
import { INK, NEUTRAL, STATUS_BADGE } from "@/theme/tokens";
import { StaffAutoComplete } from "@/ui/Inputs/StaffAutoComplete";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import SystemDialog from "@/ui/Popups/Dialog";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { getEnabledNurseJobs, sortJobsByDisplayName } from "@/utils/jobs";
import { inverseRolesMap } from "@/utils/utilFunctions";
import { WarningDialog } from "@/utils/warningDialogs/WarningDialog";
import { EditTimePopover } from "../EditTimePopover";
import { LocationPicker } from "../LocationPicker/LocationPicker";
import { RecurringModalVersionToggle } from "./RecurringModalVersionToggle";
import { DaysInWeekSelector } from "./util/DaysInWeekSelector";
import { DeleteUpdateDateSelection } from "./util/DeleteUpdateDateSelection";
import { LoadingLayout } from "./util/LoadingLayout";
import { type RecurringShiftData, RecurringShiftDisplayMode, recurrenceTypeOptions, useRecurringShiftState } from "./util/useRecurringShiftState";

const typographyHeaderStyle: CSSProperties = {
	fontWeight: 600,
	fontSize: "26px",
	lineHeight: "100%",
	// brand fallback — does not re-tint with user theme
	color: STATUS_BADGE.BRAND_FALLBACK,
};

const typographyDefaultStyle: CSSProperties = {
	fontWeight: 500,
	fontSize: "14px",
	lineHeight: "100%",
	color: INK.SECONDARY,
};

const backgroundContainerColor = alpha(NEUTRAL[150], 0.5);
const largeFieldWidth = "390px";
const smallFieldWidth = "236px";
const timeLabelWidth = "60px";
const buttonsWidth = "91px";
const padding = 4;

export enum RecurringShiftComponentSource {
	MasterSchedule = "master_schedule",
	Other = "other",
}

type Props = {
	onClose?: (didUpdateOrCreate?: boolean) => void;
	initialRecurringShiftData?: RecurringShiftData;
	recurringShiftDisplayMode?: RecurringShiftDisplayMode;
	existingShiftStartTime?: DateTime;
	existingShiftNurseId?: number;
	existingRecurringShiftId?: number;
	clickedDate: DateTime;
	source?: RecurringShiftComponentSource;
	onVersionChange?: ((version: "new" | "old") => void) | Dispatch<SetStateAction<"new" | "old">>;
};

export const initialRecurringShiftDataStructure: RecurringShiftData = {
	selectedNurse: null,
	selectedLocation: undefined,
	recurrenceType: recurrenceTypeOptions[2].value,
	recurrenceValue: 1,
	consecutiveValues: {
		workingDays: 4,
		restDays: 2,
	},
};

export const RecurringShift: FC<Props> = ({
	onClose,
	initialRecurringShiftData = initialRecurringShiftDataStructure,
	recurringShiftDisplayMode = RecurringShiftDisplayMode.Create,
	existingRecurringShiftId,
	existingShiftStartTime,
	existingShiftNurseId,
	clickedDate,
	source = RecurringShiftComponentSource.Other,
	onVersionChange,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const enabledJobs = useJobs();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const {
		userInfo: { id: userId },
	} = useAuth();

	const savedRecurrenceType = localStorageService.getRecurringShiftSavedRecurrenceType(userId);
	const evaluatedInitialRecurringShiftData: RecurringShiftData = {
		...initialRecurringShiftData,
		recurrenceType: savedRecurrenceType != null ? savedRecurrenceType : initialRecurringShiftData.recurrenceType,
	};
	const {
		recurringShiftData,
		nurseListChangeHandler,
		nursesList,
		timeStartEndHeight,
		boxRef,
		recurrenceTypeOptions,
		locationChangeHandler,
		startDateChangeHandler,
		startDateValidation,
		startDateValue,
		startTimeValue,
		endTimeValue,
		endDateValue,
		endDateValidation,
		endDateChangeHandler,
		shiftDurationString,
		recurrenceValue,
		recurrenceChangeHandler,
		recurrenceTypeChangeHandler,
		daysInWeekChangeHandler,
		summaryString,
		onCancelClick,
		onSaveClick,
		onCancelConfirm,
		onSaveConfirm,
		warning,
		onWarningClose,
		saveEnabled,
		editTimePickerClicked,
		anchorRef,
		isEditingTime,
		handleTimePickerValueChange,
		toggleIsEditingTime,
		onEditDateSelectionChange,
		isFetching,
		saveLoading,
		consecutiveDaysWorkingDaysChangeHandler,
		consecutiveDaysRestDaysChangeHandler,
		consecutiveDaysWorkingDaysValue,
		consecutiveDaysRestDaysValue,
		startDateEnabled,
		nurseSelectionDisabled,
		selectedJobId,
		setSelectedJobId,
		clearEndDate,
		onEditDateSelectionChangeValid,
		maximumValueForRecurrence,
		dateForUpdate,
		warningState,
		datesExceedingLimit,
		handleDailyHoursConfirm,
		handleCancelWarnings,
	} = useRecurringShiftState({
		onClose,
		initialRecurringShiftData: evaluatedInitialRecurringShiftData,
		recurringShiftDisplayMode,
		existingShiftStartTime,
		existingShiftNurseId,
		existingRecurringShiftId,
		clickedDate,
		source,
	});

	// Edit mode seeds selectedJobId from the persisted shift; keep that job in the list even if it
	// was disabled for the department after the shift was created, so it isn't silently dropped on save.
	const jobDropdownItems = useMemo(() => {
		const nurse = recurringShiftData.selectedNurse?.value;
		if (!nurse) return [];
		const jobs = sortJobsByDisplayName(getEnabledNurseJobs(nurse, enabledJobs));
		if (selectedJobId && !jobs.some((job) => job.id === selectedJobId)) {
			const persisted = chosenDepartment.jobs?.find((job) => job.id === selectedJobId);
			if (persisted) jobs.push(persisted);
		}
		return jobs.map((job) => ({ value: job.id, label: job.displayName, id: job.id }));
	}, [recurringShiftData.selectedNurse, enabledJobs, selectedJobId, chosenDepartment.jobs]);

	const disableDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		// we do not want underlying elements to handle double click events
		event.preventDefault();
		event.stopPropagation();
	}, []);

	useEffect(() => {
		if (recurringShiftData.selectedNurse?.value.scheduledDeactivationDate) {
			if (recurringShiftData.selectedNurse?.value.scheduledDeactivationDate < DateTime.fromISO(endDateValue)) {
				endDateChangeHandler(recurringShiftData.selectedNurse?.value.scheduledDeactivationDate);
			}
		}
	}, [recurringShiftData.selectedNurse, endDateValue, endDateChangeHandler]);

	return (
		<Box
			onDoubleClick={disableDoubleClick}
			sx={{
				width: "100%",
				height: "100%",
				p: padding,
			}}
		>
			{onVersionChange ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
					}}
				>
					<Typography style={typographyHeaderStyle}>
						{recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit ? "Edit " : ""}
						Recurring Shift
					</Typography>
					<RecurringModalVersionToggle version={"old"} onVersionChange={onVersionChange} />
				</Box>
			) : (
				<Typography style={typographyHeaderStyle}>
					{recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit ? "Edit " : ""}
					Recurring Shift
				</Typography>
			)}
			{isFetching ? (
				<LoadingLayout />
			) : (
				<Fragment>
					<Box
						sx={{
							display: "flex",
							width: "100%",
							alignContent: "center",
							justifyContent: "space-between",
							mt: padding,
						}}
					>
						<StaffAutoComplete
							nurses={nursesList}
							value={recurringShiftData.selectedNurse}
							onChange={nurseListChangeHandler}
							disabled={nurseSelectionDisabled}
							width={largeFieldWidth}
							required={true}
						/>

						<LocationPicker
							data-testid="recurring-shift-location"
							label={"Choose Location (Optional)"}
							locations={chosenDepartment.locations}
							// DEFAULT: no location picked shows the picker empty.
							selectedLocation={recurringShiftData.selectedLocation?.location ?? ""}
							nurseId={recurringShiftData.selectedNurse?.value.id ?? null}
							nurseFirstName={recurringShiftData.selectedNurse?.value.firstName}
							// A recurring shift spans many days, so no one day's staffing applies.
							parDay={null}
							onChange={locationChangeHandler}
							clearable
							formControlSx={{ width: largeFieldWidth }}
						/>
					</Box>

					{jobManagementEnabled && recurringShiftData.selectedNurse && (
						<Box
							sx={{
								display: "flex",
								width: "100%",
								mt: padding / 2,
							}}
						>
							<SystemDropdown
								items={jobDropdownItems}
								selectedItem={selectedJobId ?? ""}
								onChange={(value) => setSelectedJobId(value || undefined)}
								formControlSx={{ width: largeFieldWidth }}
								label={"Job"}
							/>
						</Box>
					)}

					<Box
						style={{
							background: backgroundContainerColor,
							borderRadius: "8px",
						}}
						sx={{
							width: "100%",
							mt: padding / 2,
							p: padding / 2,
						}}
					>
						<Box
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "start",
							}}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "start",
									alignItems: "baseline",
									mt: padding / 4,
									gap: 2,
								}}
							>
								{recurringShiftData.recurrenceType !== "ConsecutiveDays" ? (
									<Fragment>
										<Typography style={typographyDefaultStyle}>{t("repeats.every")}</Typography>
										<SystemValidationTextField
											inputType={"number"}
											formControlSx={{ width: "40px" }}
											variant={"standard"}
											value={recurrenceValue}
											onChange={recurrenceChangeHandler}
											inputProps={
												maximumValueForRecurrence
													? {
															inputProps: {
																max: maximumValueForRecurrence,
															},
														}
													: undefined
											}
										/>
									</Fragment>
								) : (
									<Fragment>
										<SystemValidationTextField
											inputType={"number"}
											variant={"standard"}
											label={"Working Days amount"}
											value={consecutiveDaysWorkingDaysValue}
											onChange={consecutiveDaysWorkingDaysChangeHandler}
											formControlSx={{ width: "130px" }}
										/>
										<SystemValidationTextField
											inputType={"number"}
											variant={"standard"}
											label={"Days off amount"}
											value={consecutiveDaysRestDaysValue}
											onChange={consecutiveDaysRestDaysChangeHandler}
											formControlSx={{ width: "130px" }}
										/>
									</Fragment>
								)}
								<SystemDropdown
									items={recurrenceTypeOptions}
									selectedItem={recurringShiftData.recurrenceType}
									valueToString={(value) => value}
									onChange={recurrenceTypeChangeHandler}
								/>
							</Box>

							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "start",
									gap: 2,
									height: timeStartEndHeight,
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										border: `1px solid ${NEUTRAL[300]}`,
										borderRadius: "8px",
										p: 1,
										height: "40px",
										justifyContent: "space-between",
										cursor: "pointer",
										gap: 1,
									}}
									onClick={editTimePickerClicked}
								>
									<Typography>
										{startTimeValue ? startTimeValue : "--:--"} - {endTimeValue ? endTimeValue : "--:--"}
									</Typography>
									<AccessTimeIcon sx={{ fontSize: "16px" }} />
									<Typography style={{ color: "red" }}>*</Typography>
								</Box>
								<Typography
									style={typographyDefaultStyle}
									sx={{
										pt: 2,
										width: timeLabelWidth,
									}}
								>
									{shiftDurationString}
								</Typography>
							</Box>
						</Box>

						{recurringShiftData.recurrenceType && recurringShiftData.recurrenceType === "Weekly" && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "start",
									alignItems: "baseline",
									mt: padding / 4,
									width: "100%",
								}}
							>
								<DaysInWeekSelector selectedDays={recurringShiftData.selectedDays} onChange={daysInWeekChangeHandler} required />
							</Box>
						)}
						<Box
							ref={boxRef}
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "center",
								gap: 2,
								mt: padding,
							}}
						>
							<SystemValidationTextField
								label={t("start.date")}
								required
								inputType={"date"}
								value={startDateValue}
								onChange={startDateChangeHandler}
								validation={startDateValidation}
								formControlSx={{ width: smallFieldWidth }}
								disabled={!startDateEnabled}
								inputProps={{
									inputProps: {
										min: (existingShiftStartTime ?? DateTime.now().minus({ weeks: 1 })).toFormat("yyyy-MM-dd"),
									},
								}}
							/>
							{false && source !== RecurringShiftComponentSource.MasterSchedule /*TODO - not sure if we should remove end date completely*/ && (
								<SystemValidationTextField
									label={t("end.date")}
									inputType={"date"}
									value={endDateValue}
									onChange={(e) => endDateChangeHandler(e.target.value)}
									validation={endDateValidation}
									formControlSx={{ width: smallFieldWidth }}
									inputProps={{
										inputProps: {
											min: startDateValue,
											max: recurringShiftData.selectedNurse?.value.scheduledDeactivationDate?.toFormat("yyyy-MM-dd"),
										},
									}}
									onClear={clearEndDate}
								/>
							)}
						</Box>

						<Typography
							style={typographyDefaultStyle}
							sx={{
								mt: padding,
							}}
						>
							{summaryString}
						</Typography>
					</Box>

					<Box
						sx={{
							display: "flex",
							justifyContent: "end",
							alignItems: recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit ? "start" : "center",
							mt: padding * 2,
							gap: padding / 2,
						}}
					>
						{recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit && (
							<DeleteUpdateDateSelection
								initialDate={dateForUpdate}
								minimalDate={recurringShiftData.startDate}
								onDateSelectionChange={onEditDateSelectionChange}
								mode={"edit"}
								onValidChange={onEditDateSelectionChangeValid}
							/>
						)}
						<SystemButton buttonVariant={"secondary"} sx={{ width: buttonsWidth }} onClick={onCancelClick}>
							Cancel
						</SystemButton>
						<SystemButton
							sx={{ width: buttonsWidth }}
							onClick={onSaveClick}
							disabled={!saveEnabled || chosenDepartment.disable_edit_master_schedule}
							loading={saveLoading}
							tooltip={chosenDepartment.disable_edit_master_schedule ? t("schedule.template.edit.blocked") : undefined}
						>
							Save
						</SystemButton>
					</Box>

					{isEditingTime && anchorRef && (
						<EditTimePopover
							role={recurringShiftData.selectedNurse?.value ? inverseRolesMap[recurringShiftData.selectedNurse.value.role] : undefined}
							jobId={selectedJobId}
							startTime={recurringShiftData.shiftStart}
							endTime={recurringShiftData.shiftEnd}
							onTimeChange={handleTimePickerValueChange}
							onClose={toggleIsEditingTime}
							anchorEl={anchorRef}
							isActual={false}
						/>
					)}

					{warning && (
						<SystemDialog
							open={!!warning}
							onClose={onWarningClose}
							title={`${warning === "Cancel" ? "Discard Changes" : "Save Recurring Shift"} `}
							body={
								warning === "Cancel"
									? "Are you sure you want to discard your changes to this recurring shift?"
									: "Are you sure you want to save this recurring shift?"
							}
							confirm={warning === "Cancel" ? "Discard" : "Save"}
							onConfirm={warning === "Save" ? onSaveConfirm : onCancelConfirm}
						/>
					)}

					<WarningDialog
						openAgencyWarning={warningState.isAgencyWarningOpen}
						openOvertimeWarning={warningState.isOvertimeWarningOpen}
						openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
						overtimeDetails={warningState.overtimeDetails}
						dailyHoursDetails={{
							...warningState.dailyHoursDetails,
							dates: datesExceedingLimit,
						}}
						longShiftDuration={chosenDepartment.longShiftDuration}
						onCancelAgency={handleCancelWarnings}
						onConfirmAgency={() => {}}
						onCancelOvertime={handleCancelWarnings}
						onConfirmOvertime={() => {}}
						onCancelDailyHours={handleCancelWarnings}
						onConfirmDailyHours={handleDailyHoursConfirm}
					/>
				</Fragment>
			)}
		</Box>
	);
};
